import { Menu, ActionIcon, UnstyledButton, Text } from "@mantine/core";
import { modals } from "@mantine/modals";
import { showNotification } from "@mantine/notifications";
import { IconDotsVertical, IconEdit, IconTrash } from "@tabler/icons-react";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { forwardRef, PropsWithChildren } from "react";
import { NavLink } from "react-router-dom";
import { useGetAuthorizations } from "@/hooks/useGetAuthorizations";
import useUserStore from "@/modules/users/store";
import { captureException } from "@/services/log";
import { ID_CADASTROS_EMBAIXADORES } from "@/utils/constants";
import { EMBAIXADORES_PESSOAS_COLLECTION_KEY, deleteEmbaixadorPessoa } from "../../../firestore";

interface ContextMenuRowProperties {
  item: TEmbaixadorPessoa;
}

const EditButton = forwardRef<HTMLAnchorElement, PropsWithChildren<{ item: TEmbaixadorPessoa }>>(
  ({ item, children, ...properties }, reference) => (
    <UnstyledButton component={NavLink} to={`/embaixadores/${item.id}`} ref={reference} {...properties}>
      {children}
    </UnstyledButton>
  )
);

EditButton.displayName = "EditButton";

const ContextMenuRow = ({ item }: ContextMenuRowProperties) => {
  const activeEmpresaId = useUserStore((state) => state.activeEmpresaId);
  const queryClient = useQueryClient();
  const { update, remove } = useGetAuthorizations(ID_CADASTROS_EMBAIXADORES);

  const { mutate: deleteEmbaixador } = useMutation({
    mutationFn: () => {
      if (!activeEmpresaId) throw new Error("Empresa não selecionada");
      return deleteEmbaixadorPessoa(activeEmpresaId, item.id);
    },
    onSuccess: () => {
      showNotification({
        message: "Embaixador excluído com sucesso!",
        color: "green"
      });
      queryClient.invalidateQueries({ queryKey: [...EMBAIXADORES_PESSOAS_COLLECTION_KEY] });
    },
    onError: (error) => {
      captureException(error, true);
      showNotification({
        message: "Erro ao excluir embaixador",
        color: "red"
      });
    }
  });

  const handleDelete = () => {
    modals.openConfirmModal({
      title: "Excluir embaixador",
      children: (
        <Text size="sm">
          Tem certeza que deseja excluir o embaixador <strong>{item.nome}</strong>? Esta ação não pode ser desfeita.
        </Text>
      ),
      labels: { confirm: "Excluir", cancel: "Cancelar" },
      confirmProps: { color: "red" },
      onConfirm: () => deleteEmbaixador()
    });
  };

  return (
    <Menu shadow="md" width={200} position="left" withArrow arrowPosition="center">
      <Menu.Target>
        <ActionIcon variant="transparent" aria-label="Menu" color="gray">
          <IconDotsVertical />
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown>
        {update && (
          <Menu.Item leftSection={<IconEdit size={14} />} component={EditButton} item={item}>
            Editar
          </Menu.Item>
        )}

        {update && remove && <Menu.Divider />}

        {remove && (
          <Menu.Item color="red" leftSection={<IconTrash size={14} />} onClick={handleDelete}>
            Remover
          </Menu.Item>
        )}
      </Menu.Dropdown>
    </Menu>
  );
};

export default ContextMenuRow;
